/* Release 6.6, Header and navigation (18_Spec_Release_6_6_Header.md).
   The port of assets/av-header-stable.js for sections/au-r66-header.liquid,
   with the same contract: the bag badge and spoken label on theme:cart:change,
   the panels (hover after 80 ms, close 220 ms after leaving, focus, arrow
   down, first tap opens on touch, Escape returns focus), the search and menu
   dialogs (inert page, focus trap, Escape, focus return,
   australia:overlay_changed), the repair code around the theme's
   <predictive-search>, and --av-header-sticky-offset (the promise line never
   counts). Added for 6.6:
   - the promise line is swapped from its section after every cart change and
     says promise_state once per page and on change;
   - html.au-r66-scrolled once the promise line has scrolled away (the bar
     becomes 64 or 56 px; CSS);
   - search results from sections/au-r66-search-results.liquid, categories
     named as the tiles name them, the no results state with the four;
   - events nav_panel_open, nav_tile_click, nav_hours_click, search_chip_click, search_result_click, search_zero, menu_open,
     menu_account_click (search_open comes from av-analytics-v53.js, which
     already sent it, now with the source spec 11 asks for). No personal data;
     never the search term itself. */
(() => {
  'use strict';

  const controllerKey = '__avStableHeaderController';
  window[controllerKey]?.destroy?.();

  const lifecycleAbort = new AbortController();
  let currentHeader = null;
  let cleanupHeader = () => {};
  let cleanupOffset = () => {};
  const root = (window.Shopify?.routes?.root || '/');
  const track = (name, payload = {}) => { try { window.AVAnalytics?.custom?.(name, payload); } catch (_) { /* analytics are optional */ } };
  const lifecycleOn = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: lifecycleAbort.signal });

  // The native element keeps its form, renderer, keyboard selection and route;
  // only the request, the result shape and the reset are ours (as in 6.5 and before).
  const repairPredictiveSearch = (element, dialog, header) => {
    if (!element?.input || !element.predictiveSearchResults) return null;
    const cats = (() => { try { return JSON.parse(header.dataset.auR66Cats || '{}'); } catch (_) { return {}; } })();
    const state = (hasResults, zero) => {
      dialog.classList.toggle('has-results', hasResults);
      dialog.classList.toggle('is-zero', zero);
    };
    const prepareResults = (section) => {
      section.querySelectorAll('[data-au-r66-result="collection"]').forEach((link) => {
        const label = cats[link.dataset.handle];
        const span = link.querySelector('[data-au-r66-cat-label]');
        if (label && span) span.textContent = label;
      });
      return section.innerHTML;
    };
    const originals = new Map();
    const replace = (name, method) => { originals.set(name, Object.getOwnPropertyDescriptor(element, name)); element[name] = method; };
    const nativeChange = element.onChange;
    const nativeClose = element.close;
    const nativeRender = element.renderSearchResults;
    let sequence = 0, disposed = false, queryTimer = 0;
    const abortWork = () => {
      sequence += 1;
      window.clearTimeout(queryTimer);
      queryTimer = 0;
      element.abortController.abort();
      element.abortController = new AbortController();
    };
    const cancel = () => { abortWork(); nativeClose.call(element); };
    const clearResults = () => {
      element.predictiveSearchResults.replaceChildren();
      element.removeAttribute('results');
      const status = element.querySelector('[data-predictive-search-status]');
      if (status) status.textContent = '';
      state(false, false);
    };
    const invalidate = () => {
      cancel();
      clearResults();
      if (!element.getQuery()) { element.searchTerm = ''; return; }
      queryTimer = window.setTimeout(() => {
        queryTimer = 0;
        if (!disposed && element.isConnected && !dialog.hidden) nativeChange.call(element);
      }, 150);
    };
    replace('onChange', function (event) { if (event?.type !== 'input') nativeChange.call(this); });
    replace('close', function (clearSearchTerm = false) { abortWork(); nativeClose.call(this, clearSearchTerm); });
    replace('toggleResetButton', function () { /* "Limpar" lives in the results */ });
    replace('updateSearchForTerm', function () { /* no "search for" button */ });
    const nativeSwitchOption = element.switchOption;
    replace('switchOption', function (direction) {
      if (![...this.querySelectorAll('[role="option"]')].some((node) => node.offsetParent !== null)) return;
      nativeSwitchOption.call(this, direction);
    });
    replace('reset', function () { cancel(); this.input.value = ''; this.searchTerm = ''; clearResults(); });
    // Each result link is itself the option (no link inside an option)
    replace('selectOption', function () { this.querySelector('[role="option"][aria-selected="true"]')?.click(); });
    const holdSubmit = (event) => { if (element.querySelector('[role="option"][aria-selected="true"]')) event.preventDefault(); };
    element.input.form.addEventListener('submit', holdSubmit, true);
    replace('renderSearchResults', function (markup) {
      nativeRender.call(this, markup);
      const results = this.querySelector('[data-au-r66-results]');
      const zero = results?.dataset.zero === 'true';
      state(true, zero);
      if (zero) {
        const four = dialog.querySelector('[data-au-r66-search-four]');
        const slot = results.querySelector('[data-au-r66-zero]');
        if (four && slot) {
          const copy = four.cloneNode(true);
          copy.removeAttribute('data-au-r66-search-four');
          copy.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
          copy.querySelectorAll('[aria-labelledby]').forEach((node) => node.removeAttribute('aria-labelledby'));
          copy.classList.add('au-r66-results__four');
          slot.after(copy);
        }
        track('search_zero', { term_length: this.getQuery().length });
      }
    });
    replace('getSearchResults', async function (searchTerm) {
      cancel();
      const request = sequence;
      const current = () => !disposed && this.isConnected && !dialog.hidden && request === sequence && this.getQuery() === searchTerm;
      if (!searchTerm || !current()) return;
      const queryKey = searchTerm.toLocaleLowerCase();
      this.setLiveRegionLoadingState();
      try {
        let markup = Object.prototype.hasOwnProperty.call(this.cachedResults, queryKey) ? this.cachedResults[queryKey] : null;
        if (!markup) {
          const url = `${window.theme.routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&resources[type]=product,collection,query&resources[limit]=10&resources[limit_scope]=each&resources[options][unavailable_products]=hide&section_id=au-r66-search-results`;
          const response = await fetch(url, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Predictive search ${response.status}`);
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = parsed.querySelector('#shopify-section-au-r66-search-results');
          if (!section?.querySelector('[data-predictive-search-live-region-count-value]')) throw new Error('Predictive search response shape');
          markup = prepareResults(section);
          if (!current()) return;
          this.cachedResults[queryKey] = markup;
        }
        if (current()) this.renderSearchResults(markup);
      } catch (_) {
        // Results are optional. The typed query and the GET form to /search stay.
        if (current()) { clearResults(); this.close(); }
      }
    });
    const onReset = () => {
      cancel();
      element.searchTerm = '';
      clearResults();
      window.setTimeout(() => element.input.focus({ preventScroll: true }), 0);
    };
    element.input.addEventListener('input', invalidate, true);
    element.input.form.addEventListener('reset', onReset, true);
    return {
      cancel,
      destroy() {
        disposed = true;
        cancel();
        element.input.removeEventListener('input', invalidate, true);
        element.input.form.removeEventListener('reset', onReset, true);
        element.input.form.removeEventListener('submit', holdSubmit, true);
        originals.forEach((descriptor, name) => { if (descriptor) Object.defineProperty(element, name, descriptor); else delete element[name]; });
      }
    };
  };

  const initHeader = (header) => {
    if (!header) return () => {};
    header.classList.remove('av-header--no-js');
    const abortController = new AbortController();
    const timeouts = new Set();
    const focusableSelector = 'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),summary,[tabindex]:not([tabindex="-1"])';
    const inertRecords = new Map();
    let activeModal = null;
    const on = (target, type, handler, options = {}) => target?.addEventListener(type, handler, { ...options, signal: abortController.signal });
    const after = (handler, delay) => {
      const timeout = window.setTimeout(() => { timeouts.delete(timeout); handler(); }, delay);
      timeouts.add(timeout);
      return timeout;
    };
    const visible = (el) => !!el && !!(el.offsetWidth || el.offsetHeight || el.getClientRects().length);
    /* Release 6.8 (S1): the panels, the search and the phone menu are built on
       first open from their <noscript data-au-r66-lazy> (rendered as is without
       scripts, unparsed text with them), then their inner controls are bound. */
    const built = new WeakSet();
    const build = (container, bind) => {
      if (!container || built.has(container)) return;
      built.add(container);
      container.querySelectorAll('noscript[data-au-r66-lazy]').forEach((source) => {
        const template = document.createElement('template');
        template.innerHTML = source.textContent;
        source.replaceWith(template.content);
      });
      bind?.();
    };

    /* The bag: badge and spoken label ("Sacola com 2 itens") */
    on(document, 'theme:cart:change', (event) => {
      const count = Number(event.detail?.cartCount);
      if (!Number.isInteger(count) || count < 0) return;
      header.querySelectorAll('[data-av-cart-count-link]').forEach((link) => {
        const noun = count === 1 ? link.dataset.avCartItemOne : link.dataset.avCartItemOther;
        link.setAttribute('aria-label', [link.dataset.avCartName, link.dataset.avCartWith, String(count), noun].filter(Boolean).join(' '));
        let badge = link.querySelector('.av-header-cart__count');
        if (count === 0) { badge?.remove(); return; }
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'av-header-cart__count';
          badge.setAttribute('aria-hidden', 'true');
          link.append(badge);
        }
        badge.textContent = count > 9 ? '9+' : String(count);
      });
    });

    /* Modal isolation, as before */
    const restoreBackground = () => {
      inertRecords.forEach((record, element) => {
        element.inert = record.inert;
        if (record.hadAriaHidden) element.setAttribute('aria-hidden', record.ariaHidden);
        else element.removeAttribute('aria-hidden');
      });
      inertRecords.clear();
      activeModal = null;
    };
    const isolateModal = (modal) => {
      if (!modal || activeModal === modal) return;
      restoreBackground();
      let branch = modal;
      while (branch && branch !== document.body) {
        const parent = branch.parentElement;
        if (!parent) break;
        [...parent.children].forEach((sibling) => {
          if (sibling === branch || !(sibling instanceof HTMLElement) || sibling.matches('script,style,link')) return;
          inertRecords.set(sibling, { inert: sibling.inert, hadAriaHidden: sibling.hasAttribute('aria-hidden'), ariaHidden: sibling.getAttribute('aria-hidden') || '' });
          sibling.inert = true;
          sibling.setAttribute('aria-hidden', 'true');
        });
        branch = parent;
      }
      activeModal = modal;
    };
    const releaseModal = (modal) => { if (activeModal === modal) restoreBackground(); };
    const trapFocus = (event, rootNode) => {
      if (event.key !== 'Tab' || !rootNode || rootNode.hidden) return;
      const nodes = [...rootNode.querySelectorAll(focusableSelector)].filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    const announceOverlay = (overlay, open) => document.dispatchEvent(new CustomEvent('australia:overlay_changed', { detail: { overlay, open } }));

    /* Desktop panels */
    const mega = header.querySelector('[data-av-mega-menu]');
    const megaTriggers = [...header.querySelectorAll('[data-av-mega-trigger]')];
    const megaPanels = () => [...header.querySelectorAll('[data-av-nav-group-panel]')];
    let megaTimer;
    let activeMegaTrigger;
    let restoringMegaFocus = false;
    let megaFocusFrame = 0;
    let openedBy = 'hover';
    const desktop = () => window.matchMedia('(min-width: 1024px)').matches;
    const setMegaState = (expanded, trigger) => megaTriggers.forEach((item) => item.setAttribute('aria-expanded', String(expanded && item === trigger)));
    const setActiveMegaGroup = (trigger) => {
      const group = trigger?.dataset.avNavGroup || 'comprar';
      mega?.setAttribute('data-av-active-group', group);
      megaPanels().forEach((panel) => {
        const active = panel.dataset.avNavGroupPanel === group;
        panel.hidden = !active;
      });
    };
    const openMega = (trigger, input) => {
      if (!mega || !desktop()) return;
      build(mega);
      window.clearTimeout(megaTimer);
      timeouts.delete(megaTimer);
      const next = trigger || activeMegaTrigger;
      const changed = mega.hidden || next !== activeMegaTrigger;
      activeMegaTrigger = next;
      setActiveMegaGroup(activeMegaTrigger);
      mega.hidden = false;
      setMegaState(true, activeMegaTrigger);
      document.documentElement.classList.add('av-mega-is-open');
      header.classList.add('av-header--mega-open');
      if (changed && input) track('nav_panel_open', { group: activeMegaTrigger?.dataset.avNavGroup || '', input });
    };
    const scheduleMegaOpen = (trigger, delay = 80) => {
      window.clearTimeout(megaTimer);
      timeouts.delete(megaTimer);
      megaTimer = after(() => openMega(trigger, 'hover'), delay);
    };
    const closeMega = (delay = 220, restoreFocus = false) => {
      if (!mega) return;
      window.cancelAnimationFrame(megaFocusFrame);
      megaFocusFrame = 0;
      window.clearTimeout(megaTimer);
      timeouts.delete(megaTimer);
      megaTimer = after(() => {
        mega.hidden = true;
        setMegaState(false, activeMegaTrigger);
        document.documentElement.classList.remove('av-mega-is-open');
        header.classList.remove('av-header--mega-open');
        if (restoreFocus && activeMegaTrigger?.isConnected) {
          restoringMegaFocus = true;
          activeMegaTrigger.focus();
          restoringMegaFocus = false;
        }
      }, delay);
    };
    megaTriggers.forEach((trigger) => {
      on(trigger, 'pointerenter', (event) => { if (event.pointerType === 'mouse') scheduleMegaOpen(trigger); });
      on(trigger, 'focusin', () => { if (!restoringMegaFocus && trigger.matches(':focus-visible')) openMega(trigger, 'focus'); });
      on(trigger, 'keydown', (event) => {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        openMega(trigger, 'focus');
        window.cancelAnimationFrame(megaFocusFrame);
        megaFocusFrame = window.requestAnimationFrame(() => {
          megaFocusFrame = 0;
          if (mega && !mega.hidden && activeMegaTrigger === trigger) mega.querySelector('[data-av-nav-group-panel]:not([hidden]) a[href]')?.focus();
        });
      });
      on(trigger, 'click', (event) => {
        // Touch: the first tap opens the panel, the second follows the link.
        const coarse = window.matchMedia('(hover: none), (pointer: coarse)').matches;
        if (!coarse || !desktop() || (!mega?.hidden && activeMegaTrigger === trigger)) return;
        event.preventDefault();
        openMega(trigger, 'tap');
      });
    });
    on(mega, 'pointerenter', (event) => { if (event.pointerType === 'mouse' && !mega.hidden) openMega(activeMegaTrigger); });
    on(mega, 'pointerleave', (event) => { if (event.pointerType === 'mouse') closeMega(); });
    on(mega, 'focusin', () => openMega(activeMegaTrigger));
    on(header.querySelector('.au-r66-bar'), 'pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      if (mega && !mega.contains(event.relatedTarget)) closeMega();
    });
    on(header.querySelector('[data-au-r66-scrim]'), 'click', () => closeMega(0));
    on(header.querySelector('[data-au-r66-scrim]'), 'pointerenter', (event) => { if (event.pointerType === 'mouse') closeMega(); });
    on(document, 'click', (event) => { if (mega && !mega.hidden && !header.contains(event.target)) closeMega(0); });
    on(header, 'focusout', () => {
      requestAnimationFrame(() => {
        if (mega && !mega.hidden && !header.querySelector('.au-r66-bar')?.contains(document.activeElement) && !mega.contains(document.activeElement)) closeMega(0);
      });
    });

    /* Search and the phone menu */
    const search = header.querySelector('[data-av-search-panel]');
    const searchOpeners = () => [...header.querySelectorAll('[data-av-search-open]')];
    const mobile = header.querySelector('[data-av-mobile-drawer]');
    const mobileOpen = header.querySelector('[data-av-mobile-open]');
    const mobileClose = header.querySelector('[data-av-mobile-close]');
    let searchLast;
    let mobileLast;
    let predictiveRepair = null;
    const bindSearch = () => {
      on(search.querySelector('[data-av-search-close]'), 'click', () => closeSearch());
      customElements.whenDefined('predictive-search').then(() => {
        if (!abortController.signal.aborted) predictiveRepair = repairPredictiveSearch(search.querySelector('predictive-search'), search, header);
      });
    };
    const bindMobile = () => {
      mobile.querySelectorAll('[data-av-mobile-group]').forEach((group) => {
        on(group, 'toggle', () => {
          if (!group.open) return;
          mobile.querySelectorAll('[data-av-mobile-group][open]').forEach((other) => { if (other !== group) other.removeAttribute('open'); });
          group.scrollIntoView({ block: 'nearest' });
        });
      });
    };
    const setSearchState = (expanded) => searchOpeners().forEach((button) => button.setAttribute('aria-expanded', String(expanded)));
    const setMobileState = (expanded) => mobileOpen?.setAttribute('aria-expanded', String(expanded));
    const closeSearch = ({ restoreFocus = true } = {}) => {
      if (!search) return;
      const wasOpen = !search.hidden;
      predictiveRepair?.cancel();
      search.hidden = true;
      setSearchState(false);
      document.body.classList.remove('av-search-open');
      releaseModal(search);
      if (wasOpen && restoreFocus) {
        const target = searchLast?.isConnected && visible(searchLast) ? searchLast : (visible(mobileOpen) ? mobileOpen : null);
        target?.focus({ preventScroll: true });
      }
      if (wasOpen) announceOverlay('search', false);
    };
    const closeMobile = ({ restoreFocus = true } = {}) => {
      if (!mobile) return;
      const wasOpen = !mobile.hidden || location.hash === `#${mobile.id}`;
      mobile.hidden = true;
      if (location.hash === `#${mobile.id}`) history.replaceState(null, '', location.pathname + location.search);
      setMobileState(false);
      document.body.classList.remove('av-mobile-menu-open');
      releaseModal(mobile);
      mobile.querySelectorAll('[data-av-mobile-group][open]').forEach((group) => group.removeAttribute('open'));
      if (wasOpen && restoreFocus && mobileLast?.isConnected) mobileLast.focus({ preventScroll: true });
      if (wasOpen) announceOverlay('mobile', false);
    };
    const openSearch = (event, from) => {
      if (!search) return;
      event?.preventDefault?.();
      build(search, bindSearch);
      const opener = from || event?.currentTarget || document.activeElement;
      searchLast = opener?.dataset?.auR66SearchSource === 'menu' ? mobileOpen : opener;
      closeMobile({ restoreFocus: false });
      closeMega(0);
      search.hidden = false;
      setSearchState(true);
      document.body.classList.add('av-search-open');
      isolateModal(search);
      announceOverlay('search', true);
      after(() => { if (!search.hidden) search.querySelector('[data-av-search-input]')?.focus({ preventScroll: true }); }, 30);
    };
    const openMobile = (event) => {
      if (!mobile) return;
      event?.preventDefault?.();
      build(mobile, bindMobile);
      mobileLast = mobileOpen;
      closeSearch({ restoreFocus: false });
      closeMega(0);
      mobile.hidden = false;
      setMobileState(true);
      document.body.classList.add('av-mobile-menu-open');
      isolateModal(mobile);
      announceOverlay('mobile', true);
      track('menu_open');
      after(() => { if (!mobile.hidden) mobile.querySelector('[data-av-mobile-close]')?.focus({ preventScroll: true }); }, 30);
    };
    setSearchState(false);
    setMobileState(false);
    on(header, 'click', (event) => {
      const opener = event.target.closest('[data-av-search-open]');
      if (opener) openSearch(event, opener);
    });
    on(search?.querySelector('[data-au-r66-search-scrim]'), 'click', () => closeSearch());
    on(search, 'keydown', (event) => trapFocus(event, search));
    on(mobileOpen, 'click', openMobile);
    on(mobileClose, 'click', (event) => { event.preventDefault(); closeMobile(); });
    on(mobile, 'keydown', (event) => trapFocus(event, mobile));
    // A page opened at the menu's address (the no-script link) shows it built.
    if (mobile && location.hash === `#${mobile.id}`) build(mobile, bindMobile);
    on(document, 'keydown', (event) => {
      if (event.key !== 'Escape') return;
      if (mega && !mega.hidden) closeMega(0, true);
      closeSearch();
      closeMobile();
    });
    // A wide window never keeps the phone menu open
    on(window, 'resize', () => { if (desktop() && mobile && !mobile.hidden) closeMobile({ restoreFocus: false }); });

    /* Events (spec 11) */
    on(header, 'click', (event) => {
      const tile = event.target.closest('[data-au-r66-tile]');
      if (tile) track('nav_tile_click', { group: tile.closest('[data-av-mobile-drawer]') ? 'menu' : 'comprar', label: tile.dataset.label || '', position: Number(tile.dataset.position) || 0 });
      const hours = event.target.closest('[data-au-r66-hours]');
      if (hours) track('nav_hours_click', { label: hours.dataset.label || '' });
      const chip = event.target.closest('[data-au-r66-chip]');
      if (chip) track('search_chip_click', { label: chip.dataset.label || '' });
      const result = event.target.closest('[data-au-r66-result]');
      if (result && result.dataset.auR66Result !== 'all') track('search_result_click', { type: result.dataset.auR66Result, position: Number(result.dataset.position) || 0 });
      const account = event.target.closest('[data-au-r66-account]');
      if (account) track('menu_account_click', { link: account.dataset.auR66Account });
    });

    return () => {
      abortController.abort();
      predictiveRepair?.destroy();
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      timeouts.clear();
      window.clearTimeout(megaTimer);
      window.cancelAnimationFrame(megaFocusFrame);
      restoreBackground();
      document.body.classList.remove('av-search-open', 'av-mobile-menu-open');
      document.documentElement.classList.remove('av-mega-is-open');
      header.classList.remove('av-header--mega-open');
      setSearchState(false);
      setMobileState(false);
    };
  };

  /* --av-header-sticky-offset, as before: bars fixed or in flow at the top of
     the page push the sticky header down. The promise line scrolls away with
     the page and is never counted (spec 2.8). */
  const initStickyOffset = (header) => {
    const rootEl = document.documentElement;
    if (!header) { rootEl.style.setProperty('--av-header-sticky-offset', '0px'); return () => {}; }
    const abortController = new AbortController();
    const selectors = '[data-announcement-bar],[class*="announcement"],[class*="utility-bar"],[class*="support-bar"],[class*="top-bar"],[class*="topbar"]';
    let frame = 0;
    let observer;
    const candidates = () => [...document.querySelectorAll(selectors)].filter((node) => node !== header && !node.contains(header) && !header.contains(node) && !node.closest('.pswp, dialog, [role="dialog"], [data-au-r66-promise]'));
    const update = () => {
      frame = 0;
      let offset = 0;
      candidates().forEach((node) => {
        const style = getComputedStyle(node);
        const rect = node.getBoundingClientRect();
        if (style.display === 'none' || style.visibility === 'hidden' || rect.height <= 0 || rect.height > 96 || rect.bottom <= 0 || rect.top > 96) return;
        const fixedAtTop = (style.position === 'fixed' || style.position === 'sticky') && rect.top <= 2;
        if (fixedAtTop) offset = Math.max(offset, Math.round(rect.bottom));
      });
      const nextOffset = `${Math.max(0, offset)}px`;
      if (rootEl.style.getPropertyValue('--av-header-sticky-offset') !== nextOffset) rootEl.style.setProperty('--av-header-sticky-offset', nextOffset);
    };
    const schedule = () => { if (!frame) frame = window.requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, { passive: true, signal: abortController.signal });
    window.addEventListener('resize', schedule, { passive: true, signal: abortController.signal });
    if ('ResizeObserver' in window) { observer = new ResizeObserver(schedule); candidates().forEach((node) => observer.observe(node)); }
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', update, { once: true, signal: abortController.signal });
    else update();
    return () => { abortController.abort(); observer?.disconnect(); if (frame) window.cancelAnimationFrame(frame); };
  };

  /* The promise line: scrolled state, refresh after a cart change, promise_state */
  const initPromise = () => {
    const abortController = new AbortController();
    const rootEl = document.documentElement;
    let promise = document.querySelector('[data-au-r66-promise]');
    let io = null;
    const setScrolled = (on) => rootEl.classList.toggle('au-r66-scrolled', on);
    if (promise && 'IntersectionObserver' in window) {
      io = new IntersectionObserver(([entry]) => setScrolled(!entry.isIntersecting), { threshold: 0 });
      io.observe(promise);
    } else {
      const check = () => setScrolled(window.scrollY > 0);
      window.addEventListener('scroll', check, { passive: true, signal: abortController.signal });
      check();
    }
    const say = () => { if (promise) track('promise_state', { state: promise.dataset.state || '', remaining: Number(promise.dataset.remaining) || 0 }); };
    if (document.readyState === 'complete') say();
    else window.addEventListener('load', say, { once: true, signal: abortController.signal });
    let busy = null;
    document.addEventListener('theme:cart:change', () => {
      if (!promise) return;
      const id = promise.dataset.section;
      if (!id) return;
      busy?.abort();
      busy = new AbortController();
      fetch(`${root}?sections=${encodeURIComponent(id)}`, { signal: busy.signal, headers: { Accept: 'application/json' } })
        .then((response) => (response.ok ? response.json() : null))
        .then((json) => {
          const next = json && new DOMParser().parseFromString(json[id] || '', 'text/html').querySelector('[data-au-r66-promise]');
          if (!next || !promise?.isConnected) return;
          const line = promise.querySelector('[data-au-r66-promise-line]');
          const text = next.querySelector('[data-au-r66-promise-line]')?.textContent || '';
          const changed = next.dataset.state !== promise.dataset.state || next.dataset.remaining !== promise.dataset.remaining;
          promise.dataset.state = next.dataset.state;
          promise.dataset.remaining = next.dataset.remaining;
          if (line && line.textContent !== text) line.textContent = text;
          if (changed) say();
        })
        .catch(() => { /* the line stays as it was */ });
    }, { signal: abortController.signal });
    return () => { abortController.abort(); io?.disconnect(); busy?.abort(); promise = null; };
  };

  let cleanupPromise = () => {};
  const refresh = () => {
    cleanupHeader();
    cleanupOffset();
    cleanupPromise();
    currentHeader = document.querySelector('[data-av-header]');
    cleanupHeader = initHeader(currentHeader);
    cleanupOffset = initStickyOffset(currentHeader);
    cleanupPromise = initPromise();
  };
  const destroy = () => {
    cleanupHeader();
    cleanupOffset();
    cleanupPromise();
    lifecycleAbort.abort();
    currentHeader = null;
  };
  window[controllerKey] = { destroy, refresh };
  refresh();

  lifecycleOn(document, 'shopify:section:load', (event) => {
    const target = event.target;
    const containsHeader = target?.matches?.('[data-av-header]') || target?.querySelector?.('[data-av-header], [data-au-r66-promise]');
    if (containsHeader || !currentHeader?.isConnected) refresh();
  });
  lifecycleOn(document, 'shopify:section:unload', (event) => {
    if (!currentHeader || !(event.target === currentHeader || event.target?.contains?.(currentHeader))) return;
    cleanupHeader();
    cleanupOffset();
    cleanupHeader = () => {};
    cleanupOffset = () => {};
    currentHeader = null;
  });
})();
